import { useEffect, useMemo, useRef, useState } from 'react'
import { Clock, MapPin, MessageCircle } from 'lucide-react'
import { formatarReais } from '@du/shared'
import {
  Badge, Button, Destaque, Icone, InfoBand, Input, ProductCard, SectionTitle, Skeleton, Tabs,
} from '@du/ui'
import type { Categoria, Loja, Produto } from '../lib/api.js'

const hhmm = (min: number) => `${String(Math.floor(min / 60) % 24).padStart(2, '0')}h${String(min % 60).padStart(2, '0')}`

export function Home({
  loja,
  categorias,
  carregando,
  aoEscolher,
}: {
  loja: Loja | null
  categorias: Categoria[]
  carregando: boolean
  aoEscolher: (p: Produto) => void
}) {
  const [aba, setAba] = useState('')
  const [busca, setBusca] = useState('')
  const secoes = useRef(new Map<string, HTMLElement>())

  const destaques = useMemo(
    () => categorias.flatMap((c) => c.produtos).filter((p) => p.destaque && !p.esgotado).slice(0, 6),
    [categorias],
  )

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (!termo) return categorias
    return categorias
      .map((c) => ({
        ...c,
        produtos: c.produtos.filter(
          (p) =>
            p.nome.toLowerCase().includes(termo) ||
            (p.ingredientes ?? '').toLowerCase().includes(termo),
        ),
      }))
      .filter((c) => c.produtos.length > 0)
  }, [categorias, busca])

  /**
   * Marca a aba conforme a seção visível. Sem isto, a barra de categorias
   * mente enquanto a pessoa rola — e ela perde a referência de onde está.
   */
  useEffect(() => {
    if (busca) return
    const alvos = [...secoes.current.values()]
    if (alvos.length === 0) return
    const observador = new IntersectionObserver(
      (entradas) => {
        const visivel = entradas
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (visivel?.target.id) setAba(visivel.target.id)
      },
      // A faixa superior da tela é o que conta como "seção atual": o topo fica
      // sob a barra fixa, e o fundo ainda não foi alcançado.
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 },
    )
    for (const el of alvos) observador.observe(el)
    return () => observador.disconnect()
  }, [filtradas, busca])

  const irPara = (slug: string) => {
    setAba(slug)
    secoes.current.get(slug)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const aberta = loja?.abertaDelivery || loja?.abertaBalcao

  return (
    <>
      <header className="capa">
        {loja ? (
          <>
            <p className={`capa__estado ${aberta ? 'capa__estado--aberta' : ''}`}>
              <Badge tom={aberta ? 'sucesso' : 'erro'}>{aberta ? 'Aberto agora' : 'Fechado'}</Badge>
              {loja.deliveryPausado && loja.abertaBalcao ? (
                <span>entregas pausadas — só retirada</span>
              ) : null}
            </p>
            <p className="capa__info">
              <Icone de={MapPin} tamanho={16} /> {loja.endereco.rua}, {loja.endereco.numero} —{' '}
              {loja.endereco.bairro}, {loja.endereco.cidade}/{loja.endereco.uf}
            </p>
            <p className="capa__info">
              <Icone de={Clock} tamanho={16} />
              {loja.horarios
                .filter((h) => h.canal === 'DELIVERY' && h.diaSemana === 1)
                .map((h) => `Delivery ${hhmm(h.abreMin)} às ${hhmm(h.fechaMin)}`)
                .join('')}{' '}
              · seg a sáb
            </p>
          </>
        ) : (
          <Skeleton altura={60} />
        )}
      </header>

      {destaques.length > 0 ? (
        <section className="bloco">
          <Destaque>Os mais pedidos!</Destaque>
          <div className="vitrine">
            {destaques.map((p) => (
              <div className="vitrine__item" key={p.id}>
                <ProductCard
                  nome={p.nome}
                  precoCents={p.precoCents}
                  ingredientes={p.ingredientes}
                  fotoUrl={p.fotoUrl}
                  destaque
                  selo
                  onClick={() => aoEscolher(p)}
                />
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <div className="barra-categorias">
        <Input
          rotulo="Buscar no cardápio"
          placeholder="X-Tudo, batata, beirute…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          type="search"
          inputMode="search"
        />
        {!busca ? (
          <Tabs
            itens={categorias.map((c) => ({ id: c.slug, rotulo: c.nome, contador: c.produtos.length }))}
            ativo={aba || (categorias[0]?.slug ?? '')}
            aoTrocar={irPara}
          />
        ) : null}
      </div>

      {carregando ? (
        <div className="bloco grade-produtos">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} altura={260} />
          ))}
        </div>
      ) : filtradas.length === 0 ? (
        <p className="nada">Nada encontrado para “{busca}”.</p>
      ) : (
        filtradas.map((c, i) => (
          <section
            key={c.id}
            id={c.slug}
            className="bloco"
            ref={(el) => {
              if (el) secoes.current.set(c.slug, el)
            }}
          >
            <SectionTitle variante={(['laranja', 'amarelo', 'preto'] as const)[i % 3]}>
              {c.nome}
            </SectionTitle>
            {c.descricao ? <p className="categoria__nota">{c.descricao}</p> : null}
            <div className="grade-produtos">
              {c.produtos.map((p) => (
                <ProductCard
                  key={p.id}
                  nome={p.nome}
                  precoCents={p.precoCents}
                  ingredientes={p.ingredientes}
                  fotoUrl={p.fotoUrl}
                  esgotado={p.esgotado}
                  onClick={() => aoEscolher(p)}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {loja ? (
        <footer className="rodape">
          <InfoBand tom="laranja">
            <span>{loja.telefone}</span>
            <span>{loja.whatsapp}</span>
          </InfoBand>
          <div className="rodape__acoes">
            <Button
              variante="pilula"
              icone={MessageCircle}
              onClick={() =>
                window.open(
                  `https://wa.me/${loja.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent('Olá! Quero fazer um pedido.')}`,
                  '_blank',
                  'noopener',
                )
              }
            >
              Falar no WhatsApp
            </Button>
          </div>
          <p className="rodape__legal">
            {loja.pedidoMinimoCents > 0
              ? `Pedido mínimo para entrega: ${formatarReais(loja.pedidoMinimoCents)}. `
              : ''}
            <a href="/privacidade">Política de privacidade</a>
          </p>
        </footer>
      ) : null}
    </>
  )
}
